import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { ArrowRight, Globe2, Users, MapPin } from "lucide-react";
import { SpinningIco } from "@/components/brand/SpinningIco";

/**
 * About Us page: short, founder-voice statement of what Whistll is and
 * why it exists. Kept deliberately tight: this is marketing, not a
 * memoir. Core pillars are mirrored from the landing page so anyone
 * arriving here via footer / search has the same mental model.
 */
export function AboutPage() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/60">
        <div
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-16 -z-10 h-[520px] w-[520px] -translate-x-1/2 rounded-full opacity-20 blur-3xl"
          style={{
            backgroundImage:
              "radial-gradient(closest-side, hsl(var(--volt)), transparent 70%)",
          }}
        />
        <div className="mx-auto max-w-4xl px-6 pb-10 pt-20 text-center sm:pt-28">
          <p className="mb-4 text-sm font-medium uppercase tracking-widest text-muted-foreground">
            About Whistll
          </p>
          <h1 className="text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl">
            We're building what hoopers{" "}
            <span className="text-volt">actually need.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            Pickup basketball is harder than it should be. Group chats that
            never commit, Instagram stories no one replies to, courts you only
            find out are closed when you turn up. Whistll exists to make
            playing, and competing, the easy part.
          </p>
        </div>

        {/* Action shot grounding the hero. Keeps SpinningIco in reserve
            for elsewhere on the site so the brand still breathes.
            Unsplash placeholder until we shoot our own. */}
        <div className="mx-auto mb-20 max-w-5xl px-6">
          <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl border border-border shadow-2xl">
            <img
              src="https://images.unsplash.com/photo-1608245449230-4ac19066d2d0?w=1920&q=80&auto=format&fit=crop"
              alt="Players running a pickup game on an indoor court"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div
              aria-hidden
              className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent dark:from-background/70"
            />
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="border-b border-border/60 bg-secondary/30">
        <div className="mx-auto max-w-5xl px-6 py-20">
          <div className="mb-6 flex justify-center">
            <SpinningIco size={80} />
          </div>
          <h2 className="text-center text-3xl font-semibold tracking-tight sm:text-4xl">
            One app. Five countries. One game.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-muted-foreground">
            Whistll serves players and venues across the US, UK, Canada,
            Australia, and New Zealand. Everything flows through the same
            app, with no separate accounts or cities to sign up for.
          </p>

          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            <Pillar
              icon={<Users className="h-5 w-5" />}
              title="For players"
              body="Join open runs with pre-authed payment, refunded automatically if the game doesn't fill. Build a team, issue challenges, climb the city rankings. Share your best plays."
            />
            <Pillar
              icon={<MapPin className="h-5 w-5" />}
              title="For venues"
              body="Your courts get real demand with zero manual work. We sync availability with systems like Bookteq, Skedda, CourtReserve and more, or you can use our free venue dashboard if you don't have a system. Payouts via Stripe Connect."
            />
            <Pillar
              icon={<Globe2 className="h-5 w-5" />}
              title="One community"
              body="Players travel, clubs tour, teams scout. When it's all in one app you can run at home on Monday and show up ready to compete in a different city on Thursday."
            />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="border-b border-border/60">
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              How we think about the product
            </h2>
          </div>

          <div className="space-y-8">
            <Value
              number="01"
              title="Every game is real"
              body="No ghost bookings, no no-shows without consequence, no double-booked courts. Pre-authed payments, dual-captain confirmation, and a no-show flagging system all back that up."
            />
            <Value
              number="02"
              title="Transparent money"
              body="Players see court fees up front. Venues get their full price. Whistll's fee sits on top and is never deducted from what the venue is owed."
            />
            <Value
              number="03"
              title="Built by hoopers"
              body="Every feature exists because someone was frustrated trying to organise a run the old way. If you've ever tried to split a court fee across a group chat, you already understand."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="mx-auto max-w-4xl px-6 py-20">
          <div className="rounded-2xl border border-border bg-card p-8 sm:p-12">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-2xl font-semibold tracking-tight">
                  Run a court?
                </h3>
                <p className="mt-2 text-muted-foreground">
                  We're onboarding venues right now. Your first games in the
                  app could be next week.
                </p>
              </div>
              <Button asChild size="lg">
                <Link to="/venues">
                  Partner with Whistll
                  <ArrowRight className="h-4 w-4" aria-hidden />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function Pillar({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-volt text-primary-foreground">
        {icon}
      </div>
      <h3 className="text-lg font-semibold">{title}</h3>
      <p className="text-sm leading-relaxed text-muted-foreground">{body}</p>
    </div>
  );
}

function Value({
  number,
  title,
  body,
}: {
  number: string;
  title: string;
  body: string;
}) {
  return (
    <div className="flex gap-5">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-volt/30 bg-volt-subtle text-sm font-black text-volt">
        {number}
      </span>
      <div>
        <h3 className="text-lg font-semibold">{title}</h3>
        <p className="mt-2 text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}
